import React, { useState, useEffect } from 'react';
import { Modal, Button, Skeleton, message } from 'antd';
import PropTypes from 'prop-types';
import ProList from '@ant-design/pro-list';
import { useMutation, useQuery } from '@apollo/client';
import {
  GET_MY_ROLES_AND_CURRENT_USER_ROLE,
  GET_MY_SCOPE_TYPES,
  MY_SCOPE_TYPES_IN_PROJECT,
  SELECT_USER_ROLE,
  GET_MY_CURRENT_USER_ROLE,
} from 'gql/auth';
import { GET_REPORT_SUBMISSION_FORM_DETAIL } from 'gql/reportSubmissions';
import { displayAssistantOf, displayRole } from 'utils';
import { GET_PENDING_APPROVALS } from 'gql/pendingApprovals';

const SelectUserRoleModal = ({
  visible,
  canHide,
  showButton,
  projectId,
  buttonRender,
}) => {
  // const client = useApolloClient();
  const [isModalVisible, setIsModalVisible] = useState(visible);
  const [input, setInput] = useState({
    id: '',
    source: '',
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
    onChange: (keys) => {
      // console.log({ keys });
      setSelectedRowKeys(keys);
    },
  };
  const { data, loading } = useQuery(GET_MY_ROLES_AND_CURRENT_USER_ROLE);
  // console.log({ projectId });
  const refetchQueries = [
    {
      query: GET_MY_SCOPE_TYPES,
    },
    {
      query: GET_MY_CURRENT_USER_ROLE,
    },
    {
      query: GET_PENDING_APPROVALS,
      variables: {
        ignoreNotAuthorised: true,
      },
      errorPolicy: 'all',
    },
  ];
  if (projectId) {
    refetchQueries.push(
      {
        query: MY_SCOPE_TYPES_IN_PROJECT,
        variables: { projectId, ignoreNotAuthorised: true },
      },
      {
        query: GET_REPORT_SUBMISSION_FORM_DETAIL,
        variables: { projectIds: [projectId], ignoreNotAuthorised: true },
      }
    );
  }
  const [selectUserRole, { loading: mutationLoading }] = useMutation(
    SELECT_USER_ROLE,
    {
      update(cache, { data }) {
        const newData = data?.selectUserRole.selectedUserRole;
        localStorage.setItem('token', data.selectUserRole.token);
        cache.writeQuery({
          query: GET_MY_CURRENT_USER_ROLE,
          data: {
            myCurrentUserRole: newData,
          },
        });
      },
      refetchQueries,
      onCompleted: () => {
        // client.cache.reset();
      },
    }
  );
  // console.log({ mutationLoading });
  useEffect(() => {
    if (data?.myCurrentUserRole?.id) {
      setSelectedRowKeys([data.myCurrentUserRole.id]);
    }
  }, [data, isModalVisible]);

  const handleOk = async () => {
    if (!input.id) {
      return message.error;
    }
    const { data } = await selectUserRole({
      variables: {
        input,
      },
    });
    setIsModalVisible(false);
    message.success(
      `Switch to ${displayRole(data.selectUserRole.selectedUserRole)}`
    );
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const dataSource = data?.me.assignedRoles;

  const list = (
    <ProList
      tableAlertRender={false}
      split
      metas={{
        title: {
          render: (_, userRole) => {
            return displayRole(userRole);
          },
        },
        description: {
          render: (_, userRole) => {
            return displayAssistantOf(userRole);
          },
        },
      }}
      rowKey="id"
      rowSelection={rowSelection}
      dataSource={dataSource}
      onRow={(record) => {
        return {
          onClick: () => {
            setInput({
              id: record.id,
              source: record.__typename === 'UserRole' ? 'USER' : 'PASS_ROLE',
            });
            setSelectedRowKeys([record.id]);
          },
        };
      }}
    />
  );

  return (
    <>
      {showButton ? (
        buttonRender ? (
          buttonRender(setIsModalVisible)
        ) : (
          <Button
            type="link"
            style={{ padding: 0, color: '#000000d9' }}
            onClick={() => {
              setIsModalVisible(true);
            }}
          >
            Switch User Role
          </Button>
        )
      ) : null}
      <Modal
        title="Select your role"
        visible={isModalVisible}
        closable={canHide}
        onCancel={handleCancel}
        width={1000}
        footer={[
          canHide && (
            <Button key="back" onClick={handleCancel}>
              Cancel
            </Button>
          ),
          <Button
            key="submit"
            type="primary"
            loading={mutationLoading}
            onClick={handleOk}
          >
            Submit
          </Button>,
        ]}
      >
        {loading ? (
          <Skeleton active />
        ) : (
          <>
            <p>The system detectes that you could access multiple roles.</p>
            <p>
              Please select your role to access. You can switch to another role
              after logon.
            </p>
            {list}
          </>
        )}
      </Modal>
    </>
  );
};

SelectUserRoleModal.defaultProps = {
  visible: false,
  canHide: false,
  showButton: false,
};

SelectUserRoleModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  canHide: PropTypes.bool.isRequired,
  showButton: PropTypes.bool.isRequired,
  buttonRender: PropTypes.func,
  projectId: PropTypes.string,
};

export default SelectUserRoleModal;
